import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
} from "../../../components/ui";
import {
  fetchCrmRoles,
  fetchCrmRoleMailboxes,
  createCrmRoleMailbox,
  updateCrmRoleMailbox,
  deleteCrmRoleMailbox,
} from "../../../services/crmSettings";
import { getAuth, onAuthStateChanged } from "firebase/auth";

export default function CrmRoleMailboxesSettings() {
  const [items, setItems] = useState([]);
  const [crmRoles, setCrmRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    id: null,
    role_code: "",
    mailbox_email: "",
    mailbox_label: "",
    is_primary: true,
    is_active: true,
  });

  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authReady || !isLoggedIn) return;
    load();
  }, [authReady, isLoggedIn]);

  async function load() {
    try {
      setLoading(true);
      setError("");

      const [mailboxRows, crmRoleRows] = await Promise.all([
        fetchCrmRoleMailboxes(),
        fetchCrmRoles(),
      ]);

      setItems(Array.isArray(mailboxRows) ? mailboxRows : []);
      setCrmRoles(Array.isArray(crmRoleRows) ? crmRoleRows : []);
    } catch (err) {
      setError(err.message || "Failed to load CRM role mailboxes");
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm({
      id: null,
      role_code: "",
      mailbox_email: "",
      mailbox_label: "",
      is_primary: true,
      is_active: true,
    });
  }

  function startEdit(item) {
    setForm({
      id: item.id,
      role_code: item.role_code || "",
      mailbox_email: item.mailbox_email || "",
      mailbox_label: item.mailbox_label || "",
      is_primary: !!item.is_primary,
      is_active: !!item.is_active,
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.role_code || !form.mailbox_email.trim()) {
      setError("CRM role and mailbox email are required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        role_code: form.role_code,
        mailbox_email: form.mailbox_email.trim(),
        mailbox_label: form.mailbox_label.trim() || null,
        is_primary: form.is_primary ? 1 : 0,
        is_active: form.is_active ? 1 : 0,
      };

      if (form.id) {
        await updateCrmRoleMailbox(form.id, payload);
      } else {
        await createCrmRoleMailbox(payload);
      }

      await load();
      resetForm();
    } catch (err) {
      setError(err.message || "Failed to save CRM role mailbox");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <Card className="xl:col-span-2">
        <CardHeader>
          <CardTitle>CRM Role Mailboxes</CardTitle>
          <CardDescription>
            Configure shared mailboxes for CRM role notifications and team communication.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : (
            <div className="space-y-2">
              {items.map((m) => (
                <div
                  key={m.id}
                  className="border rounded-lg px-4 py-3 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium">{m.mailbox_email}</div>

                    <div className="text-sm text-muted-foreground mt-1">
                      Role: {m.role_code}
                      {m.mailbox_label ? ` • ${m.mailbox_label}` : ""}
                    </div>

                    <div className="text-xs text-muted-foreground mt-2">
                      {Number(m.is_primary) === 1 ? "Primary" : "Secondary"} •{" "}
                      {Number(m.is_active) === 1 ? "Active" : "Inactive"}
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => startEdit(m)}>
                      Edit
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await deleteCrmRoleMailbox(m.id);
                          await load();
                        } catch (err) {
                          setError(err.message || "Failed to delete CRM role mailbox");
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}

              {items.length === 0 && (
                <div className="text-sm text-muted-foreground">
                  No CRM role mailboxes found.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{form.id ? "Edit role mailbox" : "New role mailbox"}</CardTitle>
          <CardDescription>Create or update shared role mailbox mapping.</CardDescription>
        </CardHeader>

        <CardContent>
          <form className="space-y-3" onSubmit={handleSubmit}>
            <div>
              <div className="text-sm mb-1">CRM role</div>
              <select
                className="h-10 w-full rounded-md border px-3 bg-white dark:bg-neutral-900"
                value={form.role_code}
                onChange={(e) => setForm((s) => ({ ...s, role_code: e.target.value }))}
              >
                <option value="">Select CRM role...</option>
                {crmRoles
                  .filter((r) => Number(r.is_active) === 1)
                  .map((r) => (
                    <option key={r.id} value={r.code}>
                      {r.label} ({r.code})
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <div className="text-sm mb-1">Mailbox email</div>
              <Input
                value={form.mailbox_email}
                onChange={(e) => setForm((s) => ({ ...s, mailbox_email: e.target.value }))}
                placeholder="sales@modularobjects.com"
              />
            </div>

            <div>
              <div className="text-sm mb-1">Label</div>
              <Input
                value={form.mailbox_label}
                onChange={(e) => setForm((s) => ({ ...s, mailbox_label: e.target.value }))}
                placeholder="Sales team inbox"
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(e) => setForm((s) => ({ ...s, is_primary: e.target.checked }))}
              />
              Primary mailbox
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
              />
              Active
            </label>

            <div className="flex justify-end gap-2 pt-2">
              {form.id && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : form.id ? "Save changes" : "Create mailbox"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}